/**
 * Empty-state component used across V2 admin pages when a list / table /
 * detail view has no data.
 *
 * Pattern: icon → headline → description → action(s).
 * Always paired with a CTA where one is meaningful — empty states should
 * give the user a next step, not a dead end.
 */
import { ReactNode } from 'react'
import { LucideIcon } from 'lucide-react'

type Props = {
  icon?: LucideIcon
  title: string
  description?: string
  /** Optional secondary line under description for hints / context. */
  hint?: string
  /** Primary CTA + optional secondary actions. */
  actions?: ReactNode
  /** Use compact when nested inside small containers (e.g. modal, sidebar). */
  compact?: boolean
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  hint,
  actions,
  compact = false,
}: Props) {
  return (
    <div
      className={
        compact
          ? 'flex flex-col items-center text-center py-8 px-4 gap-3'
          : 'flex flex-col items-center text-center py-16 px-6 gap-4'
      }
    >
      {Icon && (
        <div
          className={
            compact
              ? 'rounded-full bg-muted p-3'
              : 'rounded-full bg-muted p-4'
          }
          aria-hidden="true"
        >
          <Icon
            className={
              compact ? 'h-5 w-5 text-muted-foreground' : 'h-6 w-6 text-muted-foreground'
            }
          />
        </div>
      )}

      <div className="space-y-1.5 max-w-sm">
        <h3 className={compact ? 'text-sm font-semibold' : 'text-base font-semibold'}>
          {title}
        </h3>
        {description && (
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        )}
        {hint && <p className="text-xs text-muted-foreground/80">{hint}</p>}
      </div>

      {actions && <div className="flex items-center gap-2 mt-2">{actions}</div>}
    </div>
  )
}
